import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Send, Loader2, ExternalLink } from "lucide-react";
import client from "../lib/api";

/*
 * Victoria floating bubble — a compact, always-accessible chat handle that
 * shows on every /admin/* page. It's a lightweight sibling to
 * /admin/victoria: single-conversation, no history sidebar, no brief editor,
 * just quick Q&A on the fly. Uses the same POST /api/admin/victoria/chat
 * endpoint so answers persist to conversation history and show up in the
 * full page's sidebar.
 */

const STORE_CONV = "nexus_victoria_bubble_conv";

export default function VictoriaBubble() {
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(() => localStorage.getItem(STORE_CONV) || null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && conversationId && messages.length === 0) {
      // Lazy-load history when the user opens the bubble the first time this session
      client.get(`/admin/victoria/conversations/${conversationId}`)
        .then((r) => setMessages(r.data?.messages || []))
        .catch(() => {
          // conversation was deleted from the main page; forget it
          localStorage.removeItem(STORE_CONV);
          setConversationId(null);
        });
    }
  }, [open, conversationId, messages.length]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }); }, [messages, sending, open]);

  async function send() {
    if (!draft.trim() || sending) return;
    const text = draft;
    setDraft("");
    setSending(true);
    const optimistic = { id: `local-${Date.now()}`, role: "user", content: text };
    setMessages((m) => [...m, optimistic]);
    try {
      const r = await client.post("/admin/victoria/chat", {
        message: text,
        conversation_id: conversationId,
      });
      const { conversation_id, user_message, assistant_message } = r.data;
      setMessages((m) => {
        const filtered = m.filter((x) => x.id !== optimistic.id);
        return [...filtered, user_message, assistant_message];
      });
      if (conversation_id !== conversationId) {
        localStorage.setItem(STORE_CONV, conversation_id);
        setConversationId(conversation_id);
      }
    } catch (e) {
      setMessages((m) => [...m, {
        id: `err-${Date.now()}`,
        role: "assistant",
        content: `Error: ${e?.response?.data?.detail || e?.message || "unknown"}`,
        error: true,
      }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="victoria-bubble-open"
          className="fixed z-40 bottom-5 right-5 w-14 h-14 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#FFB800] shadow-2xl shadow-[#FF6B00]/30 hover:scale-105 transition-transform flex items-center justify-center aura-pulse"
          aria-label="Ask Victoria"
        >
          <Sparkles className="w-6 h-6 text-black" />
        </button>
      )}
      {/* Panel */}
      {open && (
        <div
          data-testid="victoria-bubble-panel"
          className="fixed z-40 bottom-5 right-5 w-[calc(100vw-2.5rem)] sm:w-96 h-[70vh] max-h-[560px] rounded-2xl bg-[#0a0a0a] border border-[#FFB800]/40 shadow-2xl shadow-[#FF6B00]/20 flex flex-col overflow-hidden"
        >
          <header className="flex items-center gap-3 px-4 py-3 border-b border-white/5 bg-gradient-to-r from-[#FF6B00]/10 to-transparent">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#FFB800] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-bold leading-tight">Victoria</p>
              <p className="text-[10px] uppercase tracking-widest text-[#FFB800]">Brand strategist</p>
            </div>
            <Link
              to="/admin/victoria"
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-white p-1"
              title="Open full workspace"
              data-testid="victoria-bubble-fullscreen"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-white p-1"
              data-testid="victoria-bubble-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && !sending && (
              <div className="text-center py-6" data-testid="bubble-empty">
                <p className="text-xs text-zinc-500 mb-3">Ask me anything about your brand or metrics.</p>
                <div className="space-y-1.5">
                  {[
                    "What's my best-performing category?",
                    "Suggest a title for my next drop.",
                    "When should I post this week?",
                  ].map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setDraft(s)}
                      data-testid={`bubble-starter-${i}`}
                      className="block w-full text-left px-3 py-2 rounded-lg text-xs text-zinc-300 hover:text-white hover:bg-white/5 border border-white/10"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`bubble-msg-${m.role}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#FF6B00] text-black rounded-br-sm"
                    : m.error
                      ? "bg-red-900/40 border border-red-500/40 text-red-200"
                      : "bg-black/60 border border-white/10 text-zinc-100 rounded-bl-sm"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="bubble-typing">
                <div className="rounded-xl px-3 py-2 bg-black/60 border border-white/10 text-[#FFB800] text-xs flex items-center gap-2">
                  <Sparkles className="w-3 h-3 animate-pulse" /> thinking…
                </div>
              </div>
            )}
          </div>

          <footer className="border-t border-white/5 p-2 bg-black/40">
            <div className="flex gap-2 items-end">
              <textarea
                data-testid="victoria-bubble-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                placeholder="Message Victoria…"
                className="flex-1 rounded-lg bg-black/60 border border-white/10 p-2 text-xs text-white resize-none focus:outline-none focus:border-[#FF6B00]/60"
              />
              <button
                data-testid="victoria-bubble-send"
                onClick={send}
                disabled={!draft.trim() || sending}
                className="h-9 w-9 rounded-lg bg-[#FF6B00] hover:bg-[#FF6B00]/90 text-black disabled:opacity-40 inline-flex items-center justify-center"
                aria-label="Send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </footer>
        </div>
      )}
    </>
  );
}
